import { Icons } from '@/components/icons';
import {
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetRoot,
  SheetTitle,
  SheetTrigger,
} from '@/components/primitives/Sheet';
import { Typography } from '@/components/primitives/Typography';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { TransText } from '@/i18n/trans/text';
import { formatDateLLL } from '@/utils/dateTime';
import type { FC, ReactNode } from 'react';
import { useCallback } from 'react';

type MobileSearchWidgetProps = {
  children: ReactNode;
};

export const MobileSearchWidget: FC<MobileSearchWidgetProps> = ({
  children,
}) => {
  const {
    isSearchOverlayOpened,
    setIsSearchOverlayOpened,
    currentSearchFormValues,
  } = useSearchWidget();

  const getDates = useCallback(() => {
    const { departureDate, returnDate } = currentSearchFormValues || {};
    const dates: Array<JSX.Element> = [];

    if (departureDate) {
      const formattedDepartureDate = formatDateLLL(departureDate);
      dates.push(<>{formattedDepartureDate}</>);
    }

    if (returnDate) {
      const formattedReturnDate = formatDateLLL(returnDate);
      dates.push(<>{formattedReturnDate}</>);
    }

    return dates.map((date, index) => (
      <span key={index}>
        {index > 0 && ' - '}
        {date}
      </span>
    ));
  }, [currentSearchFormValues]);

  return (
    <SheetRoot
      open={isSearchOverlayOpened}
      onOpenChange={setIsSearchOverlayOpened}
      modal={false}
    >
      <SheetTrigger className="mx-auto flex w-full items-stretch p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tint focus-visible:ring-offset-2">
        <Icons.search className="flex-shrink-0" />
        <div className="ml-3 flex w-full flex-col items-start overflow-hidden">
          {currentSearchFormValues?.origin &&
          currentSearchFormValues?.destination ? (
            <>
              <div className="flex items-center gap-2 overflow-hidden text-start">
                <Typography variant="subtitle" className="truncate">
                  {currentSearchFormValues.origin.cityName}
                </Typography>
                {currentSearchFormValues.returnDate ? (
                  <Icons.return className="flex-shrink-0" />
                ) : (
                  <Icons.oneWayAlt className="flex-shrink-0" />
                )}
                <Typography variant="subtitle" className="truncate">
                  {currentSearchFormValues.destination.cityName}
                </Typography>
              </div>
              <div className="flex w-full flex-row items-center gap-2 overflow-hidden">
                <Typography variant="body1" className="truncate">
                  {getDates()}
                </Typography>
                <Icons.dot className="flex-shrink-0" />
                <Typography variant="body1" className="truncate">
                  <TransText
                    i18nKey="passengersCount"
                    values={{
                      count: currentSearchFormValues.passengers.length,
                    }}
                  />
                </Typography>
              </div>
            </>
          ) : (
            <Typography variant="body1" className="truncate">
              <TransText i18nKey="pressHereToSearch" />
            </Typography>
          )}
        </div>
      </SheetTrigger>
      <SheetContent side="bottom">
        <SheetHeader
          className="flex w-full flex-row items-center justify-between"
          showClose
        >
          <SheetTitle className="sr-only">
            <TransText i18nKey="editSearch" />
          </SheetTitle>
          <SheetDescription className="sr-only">
            <TransText i18nKey="editSearch" />
          </SheetDescription>
          <div className="flex items-center gap-4">
            <Icons.search />
            <Typography variant="subtitle">
              <TransText i18nKey="editSearch" />
            </Typography>
          </div>
        </SheetHeader>
        <div className="p-4">{children}</div>
      </SheetContent>
    </SheetRoot>
  );
};
